// MYAC-773 — front producer constants for the Eventarc → command-queue migration.
// Mirrors the CMS DatasourceSyncAction payload enum so the docs the front writes
// match what the sync services consume.
import { DatasourceType } from '@/models/Datasource'

/** Actions on the per-type `datasource_sync_{type}` queues. */
export enum DatasourceSyncAction {
  FULL_SYNC = 'full_sync',
  DELTA_SYNC = 'delta_sync',
  DELETE = 'delete',
  METADATA_SYNC = 'metadata_sync',
  AUTH = 'auth',
  LIST_PROJECTS = 'list_projects',
}

/** Office-365 on-demand folder-tree expansion queue. */
export const FOLDER_DISCOVER_QUEUE = 'folder_discover'

/**
 * Map a datasource type to its command-queue id, or null for types outside the
 * migration scope — MANUAL_UPLOAD has no sync, so callers keep the legacy
 * `trigger_*` behaviour for it even when the command-queue flag is on.
 */
export function datasourceSyncQueueId(type: DatasourceType): string | null {
  switch (type) {
    case DatasourceType.GOOGLE_DRIVE:
      return 'datasource_sync_gdrive'
    case DatasourceType.OFFICE_365:
      return 'datasource_sync_o365'
    case DatasourceType.ODOO:
      return 'datasource_sync_odoo'
    case DatasourceType.QUALISHARE:
      return 'datasource_sync_qualishare'
    case DatasourceType.TRACEX:
      return 'datasource_sync_tracex'
    case DatasourceType.ATLASSIAN:
      return 'datasource_sync_atlassian'
    default:
      return null
  }
}

/**
 * MYAC-1515 — what a company admin may ask of a connection, on the per-type
 * `datasource_connection_{type}` queues under `tenants/{tid}/command_queues`.
 * `auth` both creates a connection and reconnects one.
 */
export enum DatasourceConnectionAction {
  AUTH = 'auth',
  REVOKE = 'revoke',
}

/**
 * Map a connector to the company queue that signs its connections in, or null
 * for a type that has no connection (a manual upload signs in to nothing).
 * The suffixes are those of the sync queues above.
 */
export function datasourceConnectionQueueId(type: DatasourceType): string | null {
  switch (type) {
    case DatasourceType.GOOGLE_DRIVE:
      return 'datasource_connection_gdrive'
    case DatasourceType.OFFICE_365:
      return 'datasource_connection_o365'
    case DatasourceType.ODOO:
      return 'datasource_connection_odoo'
    case DatasourceType.QUALISHARE:
      return 'datasource_connection_qualishare'
    case DatasourceType.TRACEX:
      return 'datasource_connection_tracex'
    case DatasourceType.ATLASSIAN:
      return 'datasource_connection_atlassian'
    default:
      return null
  }
}

/**
 * The legacy field a datasource of each connector flips to ask for its sign-in
 * (`_dispatchDatasourceSync` falls back to it when the command-queue producers
 * are off). Null for a type that never signs in.
 */
export function datasourceAuthTriggerField(type: DatasourceType): string | null {
  switch (type) {
    case DatasourceType.GOOGLE_DRIVE:
      return 'trigger_google_oauth'
    case DatasourceType.OFFICE_365:
      return 'trigger_office365_oauth'
    case DatasourceType.ATLASSIAN:
      return 'trigger_atlassian_oauth'
    case DatasourceType.TRACEX:
      return 'trigger_tracex_auth'
    case DatasourceType.QUALISHARE:
      return 'trigger_qualishare_auth'
    case DatasourceType.ODOO:
      return 'trigger_odoo_auth'
    default:
      return null
  }
}
